import React, { Suspense, useEffect, useRef, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux';
import { showSnackbar } from '../../../../redux/snackbarSlice';

import { durationMapping } from "../durationMapping";

import {
  Grid,
  Card,
  CardContent,
  Autocomplete,
  TextField,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ListItem,
  ListItemAvatar,
  Box,
  Button,
  Avatar,
  ListItemIcon,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
  Stack,
  ListSubheader,
  Popper,
  Grow,
  Paper,
  MenuList,
  MenuItem,
  ClickAwayListener,
} from '@mui/material';

import '@fortawesome/fontawesome-free/css/all.min.css';

import HtmlTooltip from '../../../../components/tooltip/HtmlTooltip';

import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import FunctionsRoundedIcon from '@mui/icons-material/FunctionsRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import KeyboardArrowLeftRoundedIcon from '@mui/icons-material/KeyboardArrowLeftRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import { getTypeIcon, getTypeColor } from '../../../../utils/nodeTypeMapper';
import { useLazyGetEnergyConsumptionQuery, useLazyGetSeuEnergyConsumptionQuery } from '../../../../api/energy-analysis/calculateEnergySavingsApiSlice';

import { DateTime } from 'luxon';

const formatNumber = (number) => {
  return new Intl.NumberFormat('da-DK').format(number);
};

const EnergyAnalysisSingleSeriesChart = React.lazy(() => import('remote/EnergyAnalysisSingleSeriesChart').catch(() => ({ default: () => <div>Failed to load</div> })));
const EnergyAnalysisSeuSingleSeriesChart = React.lazy(() => import('remote/EnergyAnalysisSeuSingleSeriesChart').catch(() => ({ default: () => <div>Failed to load</div> })));
const EnergyAnalyzerFilterDialog = React.lazy(() => import('remote/EnergyAnalyzerFilterDialog').catch(() => ({ default: () => <div>Failed to load</div> })));

const getFilterLabel = (type, value) => {
  switch (type) {
    case 'activeHoursFilter':
      switch (value) {
        case true:
          return 'Dagstimer';
        case false:
          return 'Nattetimer';
        default:
          return 'Alle timer';
      }
    case 'activeDaysFilter':
      switch (value) {
        case true:
          return 'Hverdag';
        case false:
          return 'Weekend';
        default:
          return 'Alle dage';
      }
    case 'activeHolidaysFilter':
      switch (value) {
        case true:
          return 'Bevar helligdage';
        case false:
          return 'Udeluk helligdage';
        default:
          return '';
      }
    default:
      return '';
  }
};

const getDurationConfig = (durationDays) => {
  return durationMapping.find((entry) => durationDays >= entry.minDays && durationDays < entry.maxDays);
};

const EnergyAnalysisTab = ({
  selectedAccount,
  selectedEnergyGroup,
  setTimestampSummary,
  duration,
  timeRange,
  focusedNode,
  focusedSeu,
  initialMode
}) => {

  const dispatch = useDispatch();

  const [nodeToAnalyse, setNodeToAnalyse] = useState(focusedNode || null);
  const [seuToAnalyse, setSeuToAnalyse] = useState(focusedSeu || null)
  const [analysisMode, setAnalysisMode] = useState(initialMode || 'node'); // or 'seu'
  const [viewMode, setViewMode] = useState('line');
  const [aggregate, setAggregate] = useState('hour');
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [seriesColors, setSeriesColors] = useState([]);

  const [energyConsumptionData, setEnergyConsumptionData] = useState(null);
  const [seuEnergyConsumptionData, setSeuEnergyConsumptionData] = useState([]);
  const [baselineSeuEnergyConsumptionData, setBaselineSeuEnergyConsumptionData] = useState(null);
  const [lazyLoadedEnergyConsumptionData, setLazyLoadedEnergyConsumptionData] = useState([]);

  const [selectedRange, setSelectedRange] = useState([
    DateTime.fromMillis(timeRange.start).setZone('Europe/Copenhagen'),
    DateTime.fromMillis(timeRange.end).setZone('Europe/Copenhagen'),
  ]);

  const [getEnergyConsumptionData, { isLoading: isLoadingEnergyConsumptionData, isFetching: isFetchingEnergyConsumptionData }] = useLazyGetEnergyConsumptionQuery();
  const [getSeuEnergyConsumptionData, { isLoading: isLoadingSeuEnergyConsumptionData, isFetching: isFetchingSeuEnergyConsumptionData }] = useLazyGetSeuEnergyConsumptionQuery();

  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
  };
  const handleCloseFilterDialog = () => setOpenFilterDialog(false);

  const [filtersOpen, setFiltersOpen] = useState(true);

  const formatDate = (date) => {
    const day = date.setLocale('da').toFormat('d.');
    const month = date.setLocale('da').toFormat('MMMM');
    return `${day} ${month}`;
  };

  const config = useMemo(() => {
    if (typeof duration !== 'number') return null;
    return getDurationConfig(duration) || null; // Ensure it never returns undefined
  }, [duration]);

  const fetchEnergyConsumption = async ({
    nodeId,
    start,
    end,
    filters,
    aggregate,
  }) => {
    try {
      const response = await getEnergyConsumptionData({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        nodeId,
        start,
        end,
        aggregate,
        ...filters,
      }).unwrap();

      return {
        ...response,
        metadata: {
          ...filters,
          id: Date.now(),
        },
      };
    } catch (error) {
      console.error("Failed to fetch energy data", error);
      dispatch(showSnackbar({ message: 'Der opstod en fejl i indhentingen af forbrugsdata', severity: 'error' }));
      return null;
    }
  };

  const handleApplyFilter = async (filters) => {
    const result = await fetchEnergyConsumption({
      nodeId: nodeToAnalyse?.energyTreeNodeId,
      start: timeRange.start,
      end: timeRange.end,
      filters,
      aggregate,
    });

    if (result && result !== null) {
      setLazyLoadedEnergyConsumptionData((prev) => [...prev, result].filter(Boolean));
    } else {
      setLazyLoadedEnergyConsumptionData([]);
    }

    const response = await fetchSeuEnergyConsumption({
      seu: seuToAnalyse,
      start: timeRange?.start,
      end: timeRange?.end,
      filters,
      aggregate
    });
    setSeuEnergyConsumptionData((prev) => [...prev, response]);
  };

  // const handleApplySeuFilter = async (filters) => {
  //   const response = await fetchSeuEnergyConsumption({
  //     seu: seuToAnalyse,
  //     start: timeRange?.start,
  //     end: timeRange?.end,
  //     filters,
  //     aggregate
  //   });
  //   setSeuEnergyConsumptionData((prev) => [...prev, response]);
  // }

  useEffect(() => {
    if (!nodeToAnalyse) return;

    const fetchData = async () => {
      try {
        const filters = {
          activeHoursFilter: undefined,
          activeDaysFilter: undefined,
          activeHolidaysFilter: undefined,
        };

        const result = await getEnergyConsumptionData({
          accountId: selectedAccount?.AccountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          nodeId: nodeToAnalyse?.energyTreeNodeId,
          start: timeRange.start,
          end: timeRange.end,
          aggregate,
          ...filters,
        }).unwrap();

        const resultWithMetadata = result
          ? {
            ...result,
            metadata: {
              ...filters,
              id: Date.now(),
            },
          }
          : null;

        setTimestampSummary(resultWithMetadata?.NodeSummary?.TimestampSummary);
        setLazyLoadedEnergyConsumptionData(resultWithMetadata ? [resultWithMetadata] : []);
        // setLazyLoadedEnergyConsumptionData((prev) => [...prev, resultWithMetadata].filter(Boolean));
      } catch (error) {
        dispatch(showSnackbar({ message: 'Der opstod en fejl i hentningen af forbrugsdata', severity: 'error' }));
        setLazyLoadedEnergyConsumptionData([]);
        setTimestampSummary(null);
      }
    };

    fetchData();
  }, [selectedAccount, selectedEnergyGroup, nodeToAnalyse]);

  const refetchLazyLoadedData = async ({ aggregate, start, end }) => {
    const updatedData = await Promise.all(
      lazyLoadedEnergyConsumptionData.map(async (entry) => {
        const { metadata } = entry;
        const filters = {
          activeDaysFilter: metadata.activeDaysFilter,
          activeHoursFilter: metadata.activeHoursFilter,
          activeHolidaysFilter: metadata.activeHolidaysFilter,
        };

        const result = await fetchEnergyConsumption({
          nodeId: nodeToAnalyse?.energyTreeNodeId, // ✅ use series-specific node
          start,
          end,
          filters,
          aggregate,
        });

        return result
          ? {
            ...result,
            metadata: {
              ...metadata, // keep ID and filters
            },
          }
          : entry; // fallback to original if fetch fails
      })
    );

    setLazyLoadedEnergyConsumptionData(updatedData.filter(Boolean));
  };

  const fetchSeuEnergyConsumption = async ({
    seu,
    start,
    end,
    filters,
    aggregate,
  }) => {
    try {
      const response = await getSeuEnergyConsumptionData({
        accountId: selectedAccount?.AccountId,
        energyGroupId: selectedEnergyGroup?.energyGroupId,
        seu,
        start,
        end,
        aggregate,
        ...filters,
      }).unwrap();

      if (response?.SEUSummary?.TimestampSummary) {
        setTimestampSummary(response.SEUSummary.TimestampSummary);
      } else {
        setTimestampSummary(null);
      }

      return {
        ...response,
        metadata: {
          ...filters,
          id: Date.now(),
        },
      };
    } catch (error) {
      console.error("Failed to fetch energy data", error);
      dispatch(showSnackbar({ message: 'Der opstod en fejl i indhentingen af SEU data', severity: 'error' }));
      return null;
    }
  };

  const refetchData = async ({ aggregate, start, end }) => {
    const updatedData = await Promise.all(
      seuEnergyConsumptionData.map(async (entry) => {
        const { metadata } = entry;
        const filters = {
          activeDaysFilter: metadata.activeDaysFilter,
          activeHoursFilter: metadata.activeHoursFilter,
          activeHolidaysFilter: metadata.activeHolidaysFilter,
        };

        const result = await fetchSeuEnergyConsumption({
          seu: seuToAnalyse,
          start,
          end,
          filters,
          aggregate,
        });

        return result; // fallback to old entry if request fails
      })
    );

    if (updatedData) {
      setSeuEnergyConsumptionData(updatedData);
    }
  };

  useEffect(() => {
    if (!selectedEnergyGroup || !nodeToAnalyse) return;

    refetchLazyLoadedData({
      aggregate,
      start: timeRange.start,
      end: timeRange.end,
    });
  }, [timeRange, aggregate, viewMode, nodeToAnalyse]);

  useEffect(() => {
    if (!nodeToAnalyse) return;

    const fetchBaselineEnergyData = async () => {
      try {
        const result = await getEnergyConsumptionData({
          accountId: selectedAccount?.AccountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          nodeId: nodeToAnalyse?.energyTreeNodeId,
          start: timeRange.start,
          end: timeRange.end,
          aggregate,
          activeHoursFilter: undefined,
          activeDaysFilter: undefined,
          activeHolidaysFilter: undefined,
        }).unwrap();

        const baselineWithMeta = {
          ...result,
          metadata: {
            id: 'BASELINE', // or keep this empty if not needed
          },
        };

        setEnergyConsumptionData(baselineWithMeta);
        setTimestampSummary(baselineWithMeta?.NodeSummary?.TimestampSummary);
      } catch (error) {
        console.error('Baseline energy fetch failed', error);
        setEnergyConsumptionData(null);
        setTimestampSummary(null);
        dispatch(showSnackbar({
          message: 'Fejl i hentning af samlet energiforbrug',
          severity: 'error'
        }));
      }
    };

    fetchBaselineEnergyData();
  }, [selectedAccount, selectedEnergyGroup, nodeToAnalyse, timeRange]);

  useEffect(() => {
    if (!seuToAnalyse) return;

    const fetchBaselineEnergyData = async () => {
      try {
        const result = await getSeuEnergyConsumptionData({
          accountId: selectedAccount?.AccountId,
          energyGroupId: selectedEnergyGroup?.energyGroupId,
          seu: seuToAnalyse,
          start: timeRange.start,
          end: timeRange.end,
          aggregate,
          activeHoursFilter: undefined,
          activeDaysFilter: undefined,
          activeHolidaysFilter: undefined,
        }).unwrap();

        const baselineWithMeta = {
          ...result,
          metadata: {
            id: 'BASELINE', // or keep this empty if not needed
          },
        };

        setBaselineSeuEnergyConsumptionData(baselineWithMeta);
        setTimestampSummary(baselineWithMeta?.SEUSummary?.TimestampSummary);
      } catch (error) {
        console.error('Baseline energy fetch failed', error);
        setBaselineSeuEnergyConsumptionData(null);
        setTimestampSummary(null);
        dispatch(showSnackbar({
          message: 'Fejl i hentning af samlet energiforbrug',
          severity: 'error'
        }));
      }
    };
    fetchBaselineEnergyData();
  }, [selectedAccount, selectedEnergyGroup, seuToAnalyse, timeRange]);

  useEffect(() => {
    if (!seuToAnalyse) return;

    const fetchData = async () => {
      const response = await fetchSeuEnergyConsumption({
        seu: seuToAnalyse,
        start: timeRange?.start,
        end: timeRange?.end,
        filters: {
          activeDaysFilter: undefined,
          activeHoursFilter: undefined,
          activeHolidaysFilter: undefined,
        },
        aggregate
      });
      if (response) {
        setSeuEnergyConsumptionData([response]);
      }
    };

    fetchData();
  }, [seuToAnalyse, selectedEnergyGroup?.energyGroupId, aggregate, viewMode, timeRange?.start, timeRange?.end, duration]);

  useEffect(() => {
    if (seuEnergyConsumptionData && seuEnergyConsumptionData.length > 0) {
      refetchData({
        seu: seuToAnalyse,
        aggregate,
        start: timeRange.start,
        end: timeRange.end,
      });
    }
  }, [timeRange, aggregate, seuToAnalyse]);


  const validChartOptions = config?.chartOptions || [];  // Available chart types
  const validXAxisOptions = config?.xAxisOptions?.[viewMode] || []; // Available X-Axis granularity

  useEffect(() => {
    if (!config) return;
    // Ensure the current `viewMode` is valid, otherwise, switch to default
    if (!validChartOptions.includes(viewMode)) {
      const defaultChart = config?.defaultChart;
      setViewMode(defaultChart);
    }

    // Ensure the current `aggregate` (X-Axis) is valid, otherwise, switch to default
    if (!validXAxisOptions.includes(aggregate)) {
      const defaultXAxis = config?.defaultXAxis?.[viewMode] || "hour";
      setAggregate(defaultXAxis);
    }

  }, [validChartOptions, validXAxisOptions, config, viewMode, aggregate]);

  const getTooltipTitle = (item) => {
    const labelMap = {
      hour: 'Time',
      day: 'Dag',
      week: 'Uge',
      month: 'Måned',
    };

    const isValidXAxis = validXAxisOptions.includes(item);

    // Tooltip logic
    if (item === 'hour' && viewMode !== 'line') {
      return 'Visning er kun tilgængelig i linjediagram';
    }

    if (['day', 'week', 'month'].includes(item) && viewMode === 'line') {
      return `Visning er kun tilgængelig i søjlediagram.`;
    }

    if (!isValidXAxis) {
      return 'Denne visning er ikke tilgængelig for den valgte periode.';
    }

    return `Vis pr. ${labelMap[item] || item}`;
  };


  // Ensure `nodeToAnalyse` starts as a valid node (fix for initial load issue)
  useEffect(() => {
    if (!nodeToAnalyse || !selectedEnergyGroup.energyTreeNodes.find(node => node.energyTreeNodeId === nodeToAnalyse.energyTreeNodeId)) {
      setNodeToAnalyse(selectedEnergyGroup.energyTreeNodes[0]);  // Set first available node as default
    }
  }, [nodeToAnalyse, selectedEnergyGroup]);

  // Create a lookup map for quick access
  const nodeLookup = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach(node => {
      if (!map.has(node.energyTreeNodeId)) {
        map.set(node.energyTreeNodeId, { ...node, children: [] });
      }
      if (node.parentId) {
        if (!map.has(node.parentId)) {
          map.set(node.parentId, { children: [] });
        }
        map.get(node.parentId).children.push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const nodeGroupsByType = useMemo(() => {
    const map = new Map();
    selectedEnergyGroup?.energyTreeNodes?.forEach(node => {
      const type = node?.type;
      if (type) {
        if (!map.has(type)) map.set(type, []);
        map.get(type).push(node);
      }
    });
    return map;
  }, [selectedEnergyGroup]);

  const children = useMemo(() => nodeLookup.get(nodeToAnalyse?.energyTreeNodeId)?.children || [], [nodeToAnalyse, nodeLookup]);
  const parent = useMemo(() => nodeLookup.get(nodeToAnalyse?.parentId) || null, [nodeToAnalyse, nodeLookup]);

  const seuInputRef = useRef(null);

  const anchorRef = useRef(null);
  const [openAggregationMenu, setOpenAggregationMenu] = useState(false);
  const [dataSeriesAggregate, setDataSeriesAggregate] = useState({ value: 'mean', label: 'Gennemsnit' });

  const handleToggleAggregationMenu = () => {
    setOpenAggregationMenu((prev) => !prev);
  };

  const handleCloseAggregationMenu = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpenAggregationMenu(false);
  };
  const handleSelectAggregation = (option) => {
    setOpenAggregationMenu(false);
    setDataSeriesAggregate(option);
  }

  const validSeuOptions = useMemo(() => {
    return Array.from(nodeGroupsByType.entries())
      .filter(([type, nodes]) =>
        nodes.some(node => node?.dataStreams?.length > 0)
      )
      .map(([type]) => type)
      .filter(type => type !== 'Hovedmåler' && type !== 'Tavle' && type !== 'Undertavle');
  }, [nodeGroupsByType]);

  const effectiveSeuToAnalyse = seuToAnalyse ?? validSeuOptions[0];

  useEffect(() => {
    if (analysisMode === 'seu' && seuInputRef.current) {
      // blur the input immediately after mounting so dropdown doesn't pop open
      seuInputRef.current.blur();
      setSeuToAnalyse(effectiveSeuToAnalyse)
    }
  }, [analysisMode]);

  const options = [
    { value: 'sum', label: 'Sum' },
    { value: 'mean', label: 'Gennemsnit' },
    { value: 'min', label: 'Minimum' },
    { value: 'max', label: 'Maksimum' },
  ];

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={filtersOpen ? 8 : 12} xl={filtersOpen ? 9 : 12}
          sx={{
            transition: 'flex-basis 0.3s ease-in-out', // smooth resize
          }}>
          <Card variant='outlined' sx={{ p: 1, borderRadius: 2 }}>
            <CardContent>

              <Stack
                direction="column"
                color="inherit"
                component={Card}
                elevation={0}
                sx={{
                  transition: 'box-shadow 0.3s ease',
                  height: '100%',
                  background: 'transparent',
                  mb: 4,
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <ListItemText
                        primary={analysisMode === 'node'
                          ? `Node`
                          : `SEU`}
                        primaryTypographyProps={{ color: 'text.secondary', variant: 'body2', }}
                        secondary={analysisMode === 'node'
                          ? `${nodeToAnalyse?.name}`
                          : `${seuToAnalyse}`}
                        secondaryTypographyProps={{ color: 'text.primary', variant: 'h6', fontWeight: 'bold' }}
                      />
                      <Tooltip title={`Summeret energiforbrug på ${nodeToAnalyse?.name}`} placement="right">
                        <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                      </Tooltip>
                    </Box>

                    {analysisMode === 'node' && (
                      <Box flexDirection={"row"} display={"flex"} alignItems={"center"}>
                        <Typography variant="h4" color="textPrimary" fontWeight="bold">
                          {energyConsumptionData ? formatNumber(energyConsumptionData.EnergySummary?.TotalEnergyUsage.toFixed(0)) : '-'} kWh
                        </Typography>
                        <Typography variant="h4" color="textPrimary" fontWeight="bold">
                        </Typography>

                        {energyConsumptionData?.EnergySummary?.NotAnalysedEnergyUsagePercentage ? (
                          <HtmlTooltip placement="right" content={
                            <ListItem>
                              <ListItemIcon>
                                <WarningRoundedIcon
                                  fontSize='large'
                                  sx={{
                                    color: "warning.light",
                                  }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                primary={energyConsumptionData?.EnergySummary?.NotAnalysedEnergyUsage ? `${formatNumber(energyConsumptionData?.EnergySummary?.NotAnalysedEnergyUsage.toFixed(0))} kWh` : ' Det er ikke muligt at beregne det ukendte forbrug'}
                                primaryTypographyProps={{
                                  variant: "h6", fontWeight: 'bold', color: 'warning.light'
                                }}
                                secondary={energyConsumptionData?.EnergySummary?.NotAnalysedEnergyUsagePercentage ? ` ${energyConsumptionData?.EnergySummary?.NotAnalysedEnergyUsagePercentage.toFixed(1)} % af forbruget er ukendt eller kan ej dokumenteres under ${nodeToAnalyse.name} - dette kan skyldes utilstrækkelig dokumentation og neddeling af forbrugere.` : "Det er ikke muligt at beregne det ukendte forbrug. Dette kan skyldes en række faktorer, herunder manglende data, utilstrækkelig identifikation af forbrugerene eller manglende referencedata."}
                                secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                              />
                            </ListItem>
                          }>
                            <IconButton variant="outlined" size="small" disableRipple onClick={(e) => e.stopPropagation()} sx={{ ml: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                              <WarningRoundedIcon
                                sx={{
                                  color: 'warning.light'
                                }}
                              />
                            </IconButton>
                          </HtmlTooltip>
                        ) : null}

                      </Box>
                    )}

                    {analysisMode === 'seu' && (
                      <Box flexDirection={"row"} display={"flex"} alignItems={"center"}>
                        <Typography variant="h4" color="textPrimary" fontWeight="bold">
                          {baselineSeuEnergyConsumptionData ? formatNumber(baselineSeuEnergyConsumptionData.EnergySummary.TotalEnergyUsage.toFixed(0)) : '-'} kWh
                        </Typography>
                        <Typography variant="h4" color="textPrimary" fontWeight="bold">
                        </Typography>

                        {/* {energyConsumptionData?.NodeSummary?.NotAnalysedEnergyUsagePercentage ? (
                            <HtmlTooltip placement="right" content={
                              <ListItem>
                                <ListItemIcon>
                                  <WarningRoundedIcon
                                    fontSize='large'
                                    sx={{
                                      color: "warning.light",
                                    }}
                                  />
                                </ListItemIcon>
                                <ListItemText
                                  primary={energyConsumptionData?.NodeSummary?.NotAnalysedEnergyUsage ? `${formatNumber(energyConsumptionData?.NodeSummary?.NotAnalysedEnergyUsage.toFixed(0))} kWh` : ' Det er ikke muligt at beregne det ukendte forbrug'}
                                  primaryTypographyProps={{
                                    variant: "h6", fontWeight: 'bold', color: 'warning.light'
                                  }}
                                  secondary={energyConsumptionData?.NodeSummary?.NotAnalysedEnergyUsagePercentage ? ` ${energyConsumptionData?.NodeSummary?.NotAnalysedEnergyUsagePercentage.toFixed(1)} % af forbruget er ukendt eller kan ej dokumenteres under ${nodeToAnalyse.name} - dette kan skyldes utilstrækkelig dokumentation og neddeling af forbrugere.` : "Det er ikke muligt at beregne det ukendte forbrug. Dette kan skyldes en række faktorer, herunder manglende data, utilstrækkelig identifikation af forbrugerene eller manglende referencedata."}
                                  secondaryTypographyProps={{ variant: 'body2', color: 'text.secondary' }}
                                />
                              </ListItem>
                            }>
                              <IconButton variant="outlined" size="small" disableRipple onClick={(e) => e.stopPropagation()} sx={{ ml: 1, p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                                <WarningRoundedIcon
                                  sx={{
                                    color: 'warning.light'
                                  }}
                                />
                              </IconButton>
                            </HtmlTooltip>
                          ) : null} */}

                      </Box>
                    )}

                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      I perioden {selectedRange[0] && selectedRange[1]
                        ? `${formatDate(selectedRange[0])} - ${formatDate(selectedRange[1])}`
                        : 'Vælg periode'}
                    </Typography>
                  </Box>

                  <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center">
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <HtmlTooltip title={aggregate === 'hour' ? "Skift af aggregering ikke mulig" : "Skift imellem aggregering"} placement="top">
                        <div>
                          <Button
                            ref={anchorRef}
                            onClick={handleToggleAggregationMenu}
                            startIcon={<FunctionsRoundedIcon />}
                            disabled={aggregate === 'hour'}
                            sx={{
                              p: 1,
                              px: 2,
                              borderRadius: 3,
                              color: 'text.primary',
                              width: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              mr: 1,
                              textTransform: 'none', // optional: to keep label casing
                            }}
                          >
                            {dataSeriesAggregate?.label ?? 'Vælg'}
                          </Button>

                        </div>
                      </HtmlTooltip>

                      <ToggleButtonGroup
                        value={viewMode}
                        exclusive
                        onChange={(event, newView) => {
                          if (newView !== null && validChartOptions.includes(newView)) {
                            setViewMode(newView);
                          }
                        }}
                      >
                        <HtmlTooltip
                          title={
                            !validChartOptions.includes('line')
                              ? 'Denne visning er ikke tilgængelig for den valgte periode.'
                              : 'Viser det samlede energiforbrug for valgte målepunkt over tid'
                          }
                          placement="top"
                        >
                          <span>
                            <ToggleButton
                              value="line"
                              size="small"
                              disabled={!validChartOptions.includes('line')}
                              sx={{ borderRadius: 3, p: 1.1}}
                            >
                              <ShowChartRoundedIcon />
                            </ToggleButton>
                          </span>
                        </HtmlTooltip>

                        <HtmlTooltip
                          title="Viser det samlede energiforbrug for valgte målepunkt"
                          placement="top"
                        >
                          <span>
                            <ToggleButton
                              value="column"
                              size="small"
                              disabled={!validChartOptions.includes('column')}
                              sx={{ borderRadius: 3, p: 1.1 }}
                            >
                              <BarChartRoundedIcon />
                            </ToggleButton>
                          </span>
                        </HtmlTooltip>

                      </ToggleButtonGroup>

                      <Popper
                        open={openAggregationMenu}
                        anchorEl={anchorRef.current}
                        placement="bottom-end"
                        transition
                        disablePortal
                        sx={{ zIndex: 1301 }}
                      >
                        {({ TransitionProps }) => (
                          <Grow {...TransitionProps} style={{ transformOrigin: 'left top' }}>
                            <Paper elevation={0} variant="outlined" sx={{ mt: 0.5, borderRadius: 2 }}>
                              <ClickAwayListener onClickAway={handleCloseAggregationMenu}>
                                <MenuList autoFocusItem={open} dense sx={{ minWidth: 150, p: 1 }}>
                                  {options.map((option) => (
                                    <MenuItem
                                      key={option.value}
                                      onClick={() => handleSelectAggregation(option)}
                                      sx={{
                                        py: '6px',
                                        px: '12px',
                                        borderRadius: '5px',
                                        '&:hover': {
                                          bgcolor: 'action.hover',
                                          transition: 'all 0.3s ease',
                                        },
                                      }}
                                    >
                                      <Box
                                        component="span"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                      >
                                        <Typography variant="body2" fontWeight="medium">
                                          {option.label}
                                        </Typography>
                                      </Box>
                                    </MenuItem>
                                  ))}
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Grow>
                        )}
                      </Popper>

                      <ToggleButtonGroup
                        value={aggregate}
                        exclusive
                        onChange={(event, newValue) => {
                          if (newValue !== null) setAggregate(newValue);
                        }}
                        fullWidth
                        sx={{ ml: 1 }}
                      >
                        {['hour', 'day', 'week', 'month'].map((item) => {
                          const isDisabled = !validXAxisOptions.includes(item);
                          return (
                            <HtmlTooltip key={item} title={getTooltipTitle(item)} placement="top">
                              {/* Wrap in span to make Tooltip work on disabled buttons */}
                              <span>
                                <ToggleButton
                                  value={item}
                                  size="small"
                                  disabled={isDisabled}
                                  sx={{
                                    textTransform: 'none',
                                    borderRadius: 3,
                                    p: 1.2,
                                    pointerEvents: isDisabled ? 'none' : 'auto' // important for Tooltip to show
                                  }}
                                >
                                  {item === 'hour' && 'Time'}
                                  {item === 'day' && 'Dag'}
                                  {item === 'week' && 'Uge'}
                                  {item === 'month' && 'Måned'}
                                </ToggleButton>
                              </span>
                            </HtmlTooltip>
                          );
                        })}
                      </ToggleButtonGroup>

                      <HtmlTooltip title={`${filtersOpen ? 'Skjul' : 'Vis'} filtre`} placement="top">
                        <IconButton
                          onClick={() => setFiltersOpen(!filtersOpen)}
                          sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, ml: 4 }}
                        >
                          {filtersOpen ? <KeyboardArrowRightRoundedIcon /> : <KeyboardArrowLeftRoundedIcon />}
                        </IconButton>
                      </HtmlTooltip>
                    </Box>
                  </Box>
                </Box>
              </Stack>
              {analysisMode === 'node' && (
                <Suspense fallback={<div>Loading...</div>}>
                  <EnergyAnalysisSingleSeriesChart                                      // Single Series Chart to slice data
                    // nodeData={energyConsumptionData}
                    lazyLoadedEnergyConsumptionData={lazyLoadedEnergyConsumptionData}
                    isLoading={isLoadingEnergyConsumptionData || isFetchingEnergyConsumptionData}
                    viewMode={viewMode}
                    aggregate={aggregate}
                    dataSeriesAggregate={dataSeriesAggregate}
                    selectedEnergyGroup={selectedEnergyGroup}
                    setSeriesColors={setSeriesColors}
                  />
                </Suspense>
              )}

              {analysisMode === 'seu' && (
                <Suspense fallback={<div>Loading...</div>}>
                  <EnergyAnalysisSeuSingleSeriesChart
                    selectedEnergyGroup={selectedEnergyGroup}
                    aggregate={aggregate}
                    timeRange={timeRange}
                    data={seuEnergyConsumptionData}
                    dataSeriesAggregate={dataSeriesAggregate}
                    isLoading={isLoadingSeuEnergyConsumptionData || isFetchingSeuEnergyConsumptionData}
                    viewMode={viewMode}
                    setSeriesColors={setSeriesColors}
                  />
                </Suspense>
              )}
            </CardContent>
          </Card>
        </Grid>

        {filtersOpen && (
          <Grid item md={4} xl={3} sx={{
            transition: 'flex-basis 0.3s ease', // smooth collapse/expand
          }}>
            <Box
              sx={{
                maxHeight: filtersOpen ? 1000 : 0, // set a maxHeight large enough to fit content
                overflow: 'hidden',
                transition: 'max-height 0.3s ease',
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      {analysisMode === 'seu' && (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body1" color="textSecondary" fontWeight="bold">
                                Serier
                              </Typography>
                              <HtmlTooltip title="Tilføj eller fjern dataserier ved at filtrere på timer, ugedage og helligdage. Det giver dig mulighed for at analysere specifikke forbrugsmønstre og sammenligne forskellige perioder." placement="left">
                                <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                              </HtmlTooltip>
                            </Box>
                            <Button
                              onClick={handleOpenFilterDialog}
                              variant="text"
                              endIcon={<AddRoundedIcon />}
                              disableElevation
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                // backgroundColor: 'hsl(0, 0%, 20%)',
                                color: 'text.secondary',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: 'text.primary',
                                },
                              }}
                            >
                              Tilføj ny serie
                            </Button>
                          </Box>

                          <List dense>
                            {seuEnergyConsumptionData?.map((series, index) => {
                              const seriesColor = seriesColors[index]?.color ?? '#ccc';

                              return (
                                <ListItem
                                  key={series?.metadata?.id || index}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    mb: 1,
                                    alignItems: 'center',
                                  }}
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      onClick={() => {
                                        const idToRemove = series?.metadata?.id;
                                        setData((prev) =>
                                          prev.filter((s) => s.metadata?.id !== idToRemove)
                                        );
                                        setSeriesColors((prev) =>
                                          prev.filter((_, i) => data[i]?.metadata?.id !== idToRemove)
                                        );
                                      }}
                                    >
                                      <DeleteRoundedIcon />
                                    </IconButton>
                                  }
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: seriesColor,
                                      mr: 2,
                                      mt: 0.5,
                                    }}
                                  />
                                  <ListItemText
                                    primary={`${seuToAnalyse} - Serie ${index + 1}`}
                                    secondary={
                                      <Typography variant="caption" component="div">
                                        <i>Anvendte filtre: </i>
                                        {[
                                          getFilterLabel('activeDaysFilter', series?.metadata?.activeDaysFilter),
                                          getFilterLabel('activeHoursFilter', series?.metadata?.activeHoursFilter),
                                          getFilterLabel('activeHolidaysFilter', series?.metadata?.activeHolidaysFilter),
                                        ]
                                          .filter(Boolean)
                                          .map((text, i) => (
                                            <span key={i}>
                                              {i > 0 && ', '}
                                              <strong>{text}</strong>
                                            </span>
                                          ))}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Box>
                      )}

                      {analysisMode === 'node' && (
                        <Box>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Typography variant="body1" color="textSecondary" fontWeight="bold">
                                Serier
                              </Typography>
                              <HtmlTooltip title="Tilføj flere serier med filtre via knappen til højre og fjern dem igen efter behov." placement="right">
                                <InfoRoundedIcon fontSize="small" sx={{ color: (theme) => theme.palette.mode === 'light' ? 'lightgray' : 'gray', ml: 1 }} />
                              </HtmlTooltip>
                            </Box>
                            <Button
                              onClick={handleOpenFilterDialog}
                              variant="text"
                              endIcon={<AddRoundedIcon />}
                              disableElevation
                              sx={{
                                textTransform: 'none',
                                borderRadius: 2,
                                color: 'text.secondary',
                                '&:hover': {
                                  backgroundColor: 'transparent',
                                  color: 'text.primary',
                                },
                              }}
                            >
                              Tilføj ny serie
                            </Button>
                          </Box>

                          <List dense>
                            {lazyLoadedEnergyConsumptionData?.map((series, index) => {
                              const seriesColor = seriesColors[index]?.color ?? '#ccc';

                              return (
                                <ListItem
                                  key={series?.metadata?.id || index}
                                  sx={{
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 2,
                                    mb: 1,
                                    alignItems: 'center',
                                  }}
                                  secondaryAction={
                                    <IconButton
                                      edge="end"
                                      onClick={() => {
                                        const idToRemove = series?.metadata?.id;
                                        setLazyLoadedEnergyConsumptionData((prev) =>
                                          prev.filter((s) => s.metadata?.id !== idToRemove)
                                        );
                                        setSeriesColors((prev) =>
                                          prev.filter((_, i) => lazyLoadedEnergyConsumptionData[i]?.metadata?.id !== idToRemove)
                                        );
                                      }}
                                    >
                                      <DeleteRoundedIcon />
                                    </IconButton>
                                  }
                                >
                                  <Box
                                    sx={{
                                      width: 12,
                                      height: 12,
                                      borderRadius: '50%',
                                      backgroundColor: seriesColor,
                                      mr: 2,
                                      mt: 0.5,
                                    }}
                                  />
                                  <ListItemText
                                    primary={`${nodeToAnalyse?.name} - Serie ${index + 1}`}
                                    secondary={
                                      <Typography variant="caption" component="div">
                                        <i>Anvendte filtre: </i>
                                        {[
                                          getFilterLabel('activeDaysFilter', series?.metadata?.activeDaysFilter),
                                          getFilterLabel('activeHoursFilter', series?.metadata?.activeHoursFilter),
                                          getFilterLabel('activeHolidaysFilter', series?.metadata?.activeHolidaysFilter),
                                        ]
                                          .filter(Boolean)
                                          .map((text, i) => (
                                            <span key={i}>
                                              {i > 0 && ', '}
                                              <strong>{text}</strong>
                                            </span>
                                          ))}
                                      </Typography>
                                    }
                                  />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs sx={{ mt: 2 }}>
                  <Card variant='outlined' sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <>
                        <Grid container spacing={2} sx={{ mb: 1, mt: 1 }}>
                          <Grid item xs>
                            {analysisMode === 'node' ? (
                              <>
                                <Autocomplete
                                  options={selectedEnergyGroup?.energyTreeNodes || []}
                                  getOptionLabel={(option) => option?.name || ""}
                                  value={nodeToAnalyse || null}
                                  onChange={(event, newValue) => {
                                    if (newValue) setNodeToAnalyse(newValue);
                                  }}
                                  disableClearable
                                  renderOption={(props, option) => (
                                    <ListItem
                                      {...props}
                                      key={option.energyTreeNodeId}
                                      sx={{
                                        '&:hover .choose-type-btn': {
                                          visibility: 'visible',
                                        },
                                        justifyContent: 'space-between',
                                      }}
                                      disableGutters
                                      secondaryAction={validSeuOptions.includes(option.type) ? (
                                        <HtmlTooltip
                                          placement="top"
                                          title={
                                            <Box display="flex" alignItems="center">
                                              <ListItemAvatar>
                                                <Avatar
                                                  variant="rounded"
                                                  sx={{
                                                    backgroundColor: getTypeColor(option.type) || "#555",
                                                    color: "#fff",
                                                    fontWeight: "bold",
                                                    borderRadius: 2
                                                  }}
                                                >
                                                  <i className={`fas ${getTypeIcon(option.type)}`}></i>
                                                </Avatar>
                                              </ListItemAvatar>
                                              <ListItemText
                                                primary={option.type}
                                                secondary={
                                                  <>
                                                    Der er fundet{' '}
                                                    <strong>
                                                      {nodeGroupsByType.get(option.type)?.length}{' '}
                                                      {nodeGroupsByType.get(option.type)?.length === 1
                                                        ? 'målepunkt'
                                                        : 'målepunkter'}
                                                    </strong>{' '}
                                                    af denne type.
                                                  </>
                                                }
                                              />
                                            </Box>
                                          }
                                        >
                                          <Box
                                            className="choose-type-btn"
                                            sx={{
                                              visibility: 'hidden',
                                              mr: 2,
                                              flexShrink: 0
                                            }}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{ color: 'text.secondary', pr: 1 }}
                                            >
                                              {option?.type}
                                            </Typography>
                                            <Typography
                                              variant="body2"
                                              color="primary"
                                              sx={{
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: 'underline' }
                                              }}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setSeuToAnalyse(option.type);
                                                setAnalysisMode('seu');
                                              }}
                                            >
                                              Vælg SEU type
                                            </Typography>
                                          </Box>
                                        </HtmlTooltip>
                                      ) : null
                                      }
                                    >
                                      <Box display="flex" alignItems="center">
                                        <ListItemAvatar>
                                          <Avatar
                                            variant="rounded"
                                            sx={{
                                              backgroundColor: getTypeColor(option.type) || "#555",
                                              color: "#fff",
                                              fontWeight: "bold",
                                              borderRadius: 2
                                            }}
                                          >
                                            <i className={`fas ${getTypeIcon(option.type)}`}></i>
                                          </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText primary={option.name} />
                                      </Box>
                                    </ListItem>


                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Vælg målepunkt"
                                      variant="outlined"
                                      fullWidth
                                      sx={{
                                        "& .MuiInputBase-root": { borderRadius: "10px" },
                                        "& .MuiOutlinedInput-root": {
                                          borderRadius: "10px",
                                          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "divider" },
                                        },
                                        "& .MuiInputLabel-root": { color: "gray" },
                                      }}
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: nodeToAnalyse ? (
                                          <InputAdornment position="start">
                                            <Avatar variant="rounded" sx={{
                                              backgroundColor: getTypeColor(nodeToAnalyse.type) || "#555",
                                              color: "#fff",
                                              fontWeight: "bold",
                                              borderRadius: 2
                                            }}><i className={`fas ${getTypeIcon(nodeToAnalyse.type)}`}></i></Avatar>
                                          </InputAdornment>
                                        ) : null,
                                      }}
                                    />
                                  )}
                                />
                                <Box mt={1} display="flex" justifyContent="flex-start">
                                  <Button
                                    variant="text"
                                    onClick={() => {
                                      setAnalysisMode('seu');
                                    }}
                                    sx={{ textTransform: 'none' }}
                                    size="small"
                                  >
                                    Vælg SEU typer
                                  </Button>
                                </Box>
                              </>
                            ) : (
                              <>
                                <Autocomplete
                                  options={validSeuOptions}
                                  getOptionLabel={(option) => option}
                                  value={seuToAnalyse}
                                  onChange={(e, newValue) => {
                                    // prevent selecting a disabled item manually
                                    setSeuToAnalyse(newValue);
                                  }}
                                  disableClearable
                                  renderOption={(props, option) => {
                                    const isDisabled = !validSeuOptions.includes(option);
                                    return (
                                      <li
                                        {...props}
                                        key={option}
                                        style={{
                                          ...props.style,
                                          opacity: isDisabled ? 0.4 : 1,
                                          pointerEvents: isDisabled ? 'none' : 'auto',
                                          cursor: isDisabled ? 'not-allowed' : 'pointer'
                                        }}
                                      >
                                        {option}
                                      </li>
                                    );
                                  }}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      label="Vælg SEU type"
                                      inputRef={seuInputRef}
                                      autoFocus={false}
                                      variant="outlined"
                                      sx={{ "& .MuiInputBase-root": { borderRadius: "10px" } }}
                                      InputProps={{
                                        ...params.InputProps,
                                        startAdornment: seuToAnalyse ? (
                                          <InputAdornment position="start">
                                            <Avatar variant="rounded" sx={{
                                              backgroundColor: getTypeColor(seuToAnalyse) || "#555",
                                              color: "#fff",
                                              fontWeight: "bold",
                                              borderRadius: 2
                                            }}>
                                              <i className={`fas ${getTypeIcon(seuToAnalyse)}`}></i>
                                            </Avatar>
                                          </InputAdornment>
                                        ) : null,
                                      }}
                                    />
                                  )}
                                />

                                <Box mt={1} display="flex" justifyContent="flex-start">
                                  <Button
                                    variant="text"
                                    onClick={() => {
                                      setAnalysisMode('node');
                                      // setSeuToAnalyse(null);
                                    }}
                                    sx={{ textTransform: 'none' }}
                                    size="small"
                                  >
                                    Vælg målepunkter
                                  </Button>
                                </Box>
                              </>
                            )}
                          </Grid>

                          <Grid item>
                            <IconButton
                              onClick={() => parent && setNodeToAnalyse(parent)}
                              disabled={!parent}
                              sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
                            >
                              <ArrowUpwardRoundedIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                        {analysisMode === 'node' && (
                          <List dense
                            sx={{
                              maxHeight: 425,
                              overflowY: 'auto',
                            }}
                            subheader={
                              <ListSubheader component="div" id="nested-list-subheader">
                                Neddelinger for {nodeToAnalyse?.name}:
                              </ListSubheader>
                            }
                          >
                            {children.length > 0 ? (
                              children.map(child => (
                                <ListItemButton key={child.id} onClick={() => setNodeToAnalyse(child)} sx={{ borderRadius: 2 }}>
                                  <ListItemIcon>
                                    <Avatar
                                      variant="rounded"
                                      size="small"
                                      sx={{
                                        backgroundColor: getTypeColor(child.type) || "#555",
                                        color: "#fff",
                                        fontWeight: "bold",
                                        borderRadius: 2
                                      }}
                                    >
                                      <i className={`fas ${getTypeIcon(child.type)}`}></i>
                                    </Avatar>
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={child.name}
                                    secondary={child.type}
                                  />
                                </ListItemButton>
                              ))
                            ) : (
                              <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: 'gray' }}>
                                Ingen yderligere neddeling
                              </Typography>
                            )}
                          </List>
                        )}
                      </>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        )}
      </Grid>

      <Suspense fallback={<div>Loading...</div>}>
        <EnergyAnalyzerFilterDialog open={openFilterDialog} handleClose={handleCloseFilterDialog} handleApply={handleApplyFilter} />
      </Suspense>
    </Box>
  );
};

export default EnergyAnalysisTab;